import { useState } from "react";
import { useQuery } from "react-query";
import { getAllMembershipBenefits } from "../../helpers/api";
import { Button, NumberInput, Text } from "@mantine/core";
import { Table, message } from "antd";
import { IconCheck } from "@tabler/icons";
import { cedisLocale } from "../../helpers/utilities";

/**
 * RedeemBenefits component (UI: utilize / value)
 *
 * Props:
 * - membershipId: string
 * - onRedeem: function(selectedBenefits) => void
 * - totalBillAmount: number - total bill amount for validation
 */
export const RedeemBenefits = ({
  membershipId,
  onRedeem,
  totalBillAmount = 0,
}) => {
  const [filteredData, setFilteredData] = useState([]);
  const [redemptionInputs, setRedemptionInputs] = useState({});
  const [selectedBenefits, setSelectedBenefits] = useState([]);

  // queries
  const { isFetching } = useQuery({
    queryFn: () => getAllMembershipBenefits(membershipId),
    queryKey: ["member-benefits", membershipId],
    onSuccess: (data) => setFilteredData(data),
  });

  // Handle utilization input change
  const handleRedemptionInputChange = (benefitId, amount) => {
    setRedemptionInputs((prev) => ({
      ...prev,
      [benefitId]: Math.max(0, amount),
    }));
  };

  // Handle utilization submission
  const handleRedeemSubmit = (benefit) => {
    if (!benefit) return;

    const amount = redemptionInputs[benefit.id] || 0;

    if (amount <= 0) {
      message.error("Please enter a valid amount to utilize");
      return;
    }

    if (amount > benefit.balance) {
      message.error("Utilization amount cannot exceed available balance");
      return;
    }

    // Calculate total selected amount including this new redemption
    const currentTotal = selectedBenefits.reduce((sum, b) => sum + b.amount, 0);
    const newTotal = currentTotal + amount;

    // Validate against total bill amount
    if (newTotal > totalBillAmount) {
      message.error(
        `Total value amount (${cedisLocale.format(
          newTotal
        )}) exceeds total bill of ${cedisLocale.format(totalBillAmount)}`
      );
      return;
    }

    // Add to selected value
    const redemptionRecord = {
      id: benefit.id,
      subscriptionId: benefit.subscriptionId,
      careCategoryId: benefit.careCategory?.id || null,
      careCategory: benefit?.careCategory || {},
      totalBenefits: benefit.totalBenefits,
      balance: benefit.balance,
      amount: amount,
      redeemedAt: new Date().toISOString(),
    };

    setSelectedBenefits((prev) => [...prev, redemptionRecord]);

    // Reset input for this value
    setRedemptionInputs((prev) => ({
      ...prev,
      [benefit.id]: 0,
    }));

    message.success(`Added ${cedisLocale.format(amount)} to utilization list`);
  };

  // Handle final submission of all selected value
  const handleFinalSubmit = () => {
    if (selectedBenefits.length === 0) {
      message.error("No value selected for utilization");
      return;
    }

    // Pass all selected value to the parent via onRedeem prop
    if (typeof onRedeem === "function") {
      onRedeem(selectedBenefits);
    }

    // Reset selections after successful utilization
    // setSelectedBenefits([]);
    // setRedemptionInputs({});
  };

  // Handle clearing all selections
  const handleClearAll = () => {
    setSelectedBenefits([]);
    setRedemptionInputs({});
    message.success("All selections cleared");
  };

  // sponsors table
  const columns = [
    {
      title: "Category",
      sorter: (a, b) => a?.careCategory?.name > b?.careCategory?.name,
      sortDirections: ["descend", "ascend"],
      render: (text, record, index) => (
        <div>{record?.careCategory?.name || "Cashless"}</div>
      ),
      width: 220,
    },
    {
      title: "Total Value",
      dataIndex: ["totalBenefits"],
      sorter: (a, b) => a?.totalBenefits > b?.totalBenefits,
      sortDirections: ["descend", "ascend"],
      render: (amount) => <div>{cedisLocale.format(amount)}</div>,
      align: "center",
      width: 150,
    },
    {
      title: "Used",
      render: (text, record) => {
        const usedAmount = record.totalBenefits - record.balance;
        return <div>{cedisLocale.format(usedAmount)}</div>;
      },
      align: "center",
      width: 150,
    },
    {
      title: "Balance",
      dataIndex: ["balance"],
      sorter: (a, b) => a?.balance > b?.balance,
      sortDirections: ["descend", "ascend"],
      render: (amount, record) => <div>{cedisLocale.format(amount)}</div>,
      align: "center",
      width: 150,
    },

    {
      title: <span>Utilize</span>,
      key: "amountToUtilize",
      render: (text, record) => (
        <NumberInput
          placeholder="0.00"
          min={0}
          max={record.balance}
          value={redemptionInputs[record.id] || ""}
          onChange={(value) => handleRedemptionInputChange(record.id, value)}
          precision={2}
          step={0.01}
          size="xs"
          disabled={record.balance <= 0}
          style={{ width: 100 }}
        />
      ),
      align: "center",
      width: 80,
    },
    {
      title: "",
      key: "action",
      render: (text, record) => (
        <Button
          size="xs"
          color="teal"
          onClick={() => handleRedeemSubmit(record)}
          disabled={
            record.balance <= 0 ||
            !redemptionInputs[record.id] ||
            redemptionInputs[record.id] <= 0 ||
            redemptionInputs[record.id] > record.balance
          }
          style={{ minWidth: 80 }}>
          <IconCheck size={14} className="me-1" />
          Utilize
        </Button>
      ),
      align: "center",
      width: 150,
    },
  ];

  return (
    <div>
      <Table
        rowKey={(record) => record.id}
        dataSource={filteredData}
        columns={columns}
        loading={isFetching}
        footer={false}
        tableLayout="auto"
        pagination={false}
        bordered
        size="middle"
        className="redeem-benefits-table"
      />

      {/* Selected Value Summary */}
      {selectedBenefits.length > 0 && (
        <div
          className="mt-4 p-3 border rounded"
          style={{ backgroundColor: "#f8f9fa" }}>
          <Text weight={600} size="sm" className="mb-2">
            Selected Value for Utilization:
          </Text>
          <div className="mb-3">
            {selectedBenefits.map((benefit, index) => (
              <div
                key={index}
                className="d-flex justify-content-between align-items-center mb-1">
                <Text size="sm">
                  {benefit.careCategory?.name || "Cashless"} -{" "}
                  {cedisLocale.format(benefit.amount)}
                </Text>
                <Button
                  size="xs"
                  color="red"
                  variant="subtle"
                  onClick={() => {
                    setSelectedBenefits((prev) =>
                      prev.filter((_, i) => i !== index)
                    );
                  }}>
                  Remove
                </Button>
              </div>
            ))}
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <Text weight={600} size="sm">
              Total:{" "}
              {cedisLocale.format(
                selectedBenefits.reduce((sum, b) => sum + b.amount, 0)
              )}
            </Text>
            <div className="d-flex gap-2">
              <Button size="sm" color="teal" onClick={handleFinalSubmit}>
                Confirm Utilization
              </Button>
              <Button size="sm" color="red" onClick={handleClearAll}>
                Clear All
              </Button>
            </div>
          </div>
        </div>
      )}

      <style>
        {`
          .redeem-benefits-table .ant-table {
            border-radius: 8px;
            border: 1px solid #e0e0e0;
            background: #fff;
          }
          .redeem-benefits-table .ant-table-thead > tr > th {
            background: #f7fafc;
            border-bottom: 1px solid #e0e0e0;
            font-weight: 600;
          }
          .redeem-benefits-table .ant-table-tbody > tr > td {
            border-bottom: 1px solid #e0e0e0;
            vertical-align: middle;
          }
          .redeem-benefits-table .ant-table-tbody > tr:last-child > td {
            border-bottom: none;
          }
        `}
      </style>
    </div>
  );
};
